import { useCallback } from 'react'
import type { Node, Edge, ReactFlowInstance } from '@xyflow/react'
import type { NodeCategory } from '@bandos/shared'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'

interface UseNodeParentingDeps {
  reactFlowInstanceRef: React.RefObject<ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null>
  reactFlowWrapperRef: React.RefObject<HTMLDivElement | null>
  executeParentNodeCreation: (nodeId: string, category: NodeCategory) => string | void
  executeRootNodeCreation: (category: NodeCategory, position?: { x: number; y: number }) => void
}

export function useNodeParenting({
  reactFlowInstanceRef,
  reactFlowWrapperRef,
  executeParentNodeCreation,
  executeRootNodeCreation,
}: UseNodeParentingDeps) {
  /**
   * Handles adding a parent node to the current node.
   * Called from node components via NodeActionsContext.
   *
   * Parent creation rules:
   * - Need nodes → creates a Venture parent directly
   * - Idea nodes → creates a Need parent directly
   */
  const handleAddParent = useCallback(
    (nodeId: string, nodeCategory: NodeCategory) => {
      if (nodeCategory === 'need') {
        executeParentNodeCreation(nodeId, 'venture')
      } else if (nodeCategory === 'idea') {
        executeParentNodeCreation(nodeId, 'need')
      } else {
        console.warn('[MindMapFlow] handleAddParent does not support category:', nodeCategory)
      }
    },
    [executeParentNodeCreation]
  )

  /**
   * Creates a root node at the center of the current viewport.
   * Used by the FAB button to place nodes where the user is looking.
   */
  const handleAddRootNodeAtViewportCenter = useCallback(
    (category: NodeCategory) => {
      if (reactFlowInstanceRef.current && reactFlowWrapperRef.current) {
        const rect = reactFlowWrapperRef.current.getBoundingClientRect()
        const containerCenterScreen = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        }
        const viewportCenterFlow =
          reactFlowInstanceRef.current.screenToFlowPosition(containerCenterScreen)
        const centeredPosition = {
          x: viewportCenterFlow.x - 140,
          y: viewportCenterFlow.y - 75,
        }
        executeRootNodeCreation(category, centeredPosition)
      } else {
        executeRootNodeCreation(category)
      }
    },
    [executeRootNodeCreation, reactFlowInstanceRef, reactFlowWrapperRef]
  )

  return { handleAddParent, handleAddRootNodeAtViewportCenter }
}
